import { valoradorPublicData, editedValoradorPublicData } from './valoradorListing';
import { generateCodename } from './codename';
import { listingPublicationErrors } from './listingPublication';

export const HANDOFF_KEY = 'uroborox.valorador.publication.v1';
const TTL = 24 * 60 * 60 * 1000;

/** Only anonymous advertisement data crosses authentication; financial answers stay in the tab. */
export const readHandoff = (storage = window.localStorage) => {
  const value = JSON.parse(storage.getItem(HANDOFF_KEY) || 'null');
  if (!value || value.version !== 1) return null;
  // An attempted write must remain recoverable even after the preparation expires.
  if (value.expiresAt <= Date.now() && (!value.attempted || value.completed)) return null;
  return value;
};

export const writeHandoff = (value, storage = window.localStorage) => {
  storage.setItem(HANDOFF_KEY, JSON.stringify(value));
  return value;
};

/** Keep a stable request and codename across repeated clicks and authentication redirects. */
export const prepareHandoff = (answers, buyerTitle, storage = window.localStorage) => {
  const publicData = valoradorPublicData(answers);
  const existing = readHandoff(storage);
  if (
    existing &&
    !existing.completed &&
    JSON.stringify(existing.sourcePublicData || existing.publicData) === JSON.stringify(publicData)
  )
    return existing;
  if (existing?.attempted && !existing.completed) throw new Error('pending');
  return writeHandoff(
    {
      version: 1,
      requestId: window.crypto.randomUUID(),
      title: publicData.listingType === 'mandato-compra' ? buyerTitle : generateCodename(),
      publicData,
      expiresAt: Date.now() + TTL,
    },
    storage
  );
};

/** Reconstruct the whitelist even if local storage has been edited. */
export const handoffPublicData = data => {
  const keys = Object.keys(
    valoradorPublicData({
      mode: data.listingType === 'mandato-compra' ? 'buyer' : 'seller',
    })
  );
  return Object.fromEntries(keys.map(key => [key, data[key]]));
};

/** Account changes require a new explicit review and may never abandon an attempted write. */
export const reassignUnsubmittedHandoff = (requestId, ownerId) => {
  const record = readHandoff();
  if (!record || record.requestId !== requestId || record.attempted || record.completed)
    throw new Error('changed');
  return writeHandoff({
    ...record,
    requestId: window.crypto.randomUUID(),
    ownerId,
  });
};

/** Save a reviewed edit under the same lock as publication; stale tabs may not overwrite it. */
export const editUnsubmittedHandoff = async ({ requestId, ownerId, publicData, buyerTitle }) => {
  if (!navigator.locks) throw new Error('environment');
  return navigator.locks.request('uroborox-valorador-publication', () => {
    const record = readHandoff();
    if (!record || record.requestId !== requestId || record.attempted || record.completed)
      throw new Error('changed');
    if (!ownerId || record.ownerId !== ownerId) throw new Error('owner');
    if (publicData.listingType !== record.publicData.listingType) throw new Error('invalid');
    const data = editedValoradorPublicData(publicData);
    const title = data.listingType === 'mandato-compra' ? buyerTitle : record.title;
    if (listingPublicationErrors({ attributes: { title, publicData: data } }).length)
      throw new Error('invalid');
    return writeHandoff({
      ...record,
      requestId: window.crypto.randomUUID(),
      title,
      sourcePublicData: record.sourcePublicData || record.publicData,
      publicData: data,
      expiresAt: Date.now() + TTL,
    });
  });
};
